import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import { useParams, useRouteMatch } from "react-router-dom";

import { getEpisodes } from "@/api/endpoints/episodes";
import { getLatestMentions, getSearchMentions } from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import { useAlert } from "@/contexts/AlertContext";
import EpisodesContext from "@/contexts/EpisodesContext";
import { useTags } from "@/contexts/TagContext";
import useFilterParams from "@/hooks/useFilterParams";
import { ROUTES } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { Episode } from "@/types/Episode";
import { Mention } from "@/types/Mention";
import { PaginatedSearchMentions } from "@/types/PaginatedSearchMentions";
import { Search } from "@/types/Search";
import { userAgentIsPhonelike } from "@/utils";

const actionTypes = {
  // Episodes are the full objects
  SET_EPISODES: "set_episode",
  PATCH_EPISODE: "patch_episode",
  DELETE_EPISODE: "delete_episode",
  // Mention refers to a single Mention
  UPDATE_MENTION: "update_mention",
  PATCH_MENTION: "patch_mention",
} as const;

type ReducerAction =
  | {
      type: "set_episode";
      payload: Episode[];
    }
  | {
      type: "patch_episode";
      payload: Partial<Episode> & { episode_uuid: string };
    }
  | { type: "delete_episode"; payload: string }
  | { type: "update_mention"; payload: Mention }
  | { type: "patch_mention"; payload: Mention };

function reducer(
  state: { episodes: Episode[] },
  action: ReducerAction
): { episodes: Episode[] } {
  switch (action.type) {
    case actionTypes.SET_EPISODES:
      return { episodes: action.payload };
    case actionTypes.UPDATE_MENTION:
      return {
        episodes: state.episodes.map((episode) => {
          return {
            // Nothing changes in the episode itself.
            ...episode,
            // Find the mention to be updated
            mentions: episode.mentions.map((mention) =>
              mention.public_id === action.payload.public_id
                ? action.payload
                : mention
            ),
          };
        }),
      };
    case actionTypes.PATCH_MENTION:
      return {
        episodes: state.episodes.map((episode) => {
          return {
            // Nothing changes in the episode itself.
            ...episode,
            // Find the mention to be updated
            mentions: episode.mentions.map((mention) =>
              mention.public_id === action.payload.public_id
                ? { ...mention, ...action.payload }
                : mention
            ),
          };
        }),
      };

    case actionTypes.PATCH_EPISODE:
      return {
        episodes: state.episodes.map((episode) => {
          return action.payload.episode_uuid === episode.episode_uuid
            ? {
                ...episode,
                ...action.payload,
              }
            : episode;
        }),
      };

    case actionTypes.DELETE_EPISODE:
      return {
        episodes: state.episodes.filter(
          (episode) => action.payload !== episode.episode_uuid
        ),
      };
    default:
      throw Error("Unknown action.");
  }
}

const EpisodesProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, {
    episodes: [],
  });
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<PaginatedSearchMentions["meta"] | null>(
    null
  );
  const { organizationTags } = useTags();
  const { uuid, slug } = useParams<{
    slug: string;
    uuid: string;
  }>();

  const [errors, setErrors] = useState<Error[]>([]);
  const [filters] = useFilterParams();
  const { alerts } = useAlert();
  // Data for the share modal
  const [shareMention, setShareMention] = useState<Episode | null>(null);
  const isFavoritePage = useRouteMatch(ROUTES.FAVORITES_ORGID)?.isExact;
  const isDashboardPage = useRouteMatch(ROUTES.DASHBOARD)?.isExact;

  // alert for alert view:
  const activeAlert = useMemo<Search | null>(
    () => alerts?.find((a) => a.uuid === uuid) || null,
    [alerts, uuid]
  );

  const activeTag = useMemo(
    // Rare use of loose equal here to allow the slug to be a number.
    () => {
      if (isFavoritePage)
        return organizationTags.find((t) => t.slug == "favorited");
      return organizationTags.find((t) => t.slug == slug);
    },
    [slug, organizationTags, isFavoritePage]
  );

  const setMentions = useCallback(
    (episodes: Episode[]) =>
      dispatch({ type: actionTypes.SET_EPISODES, payload: episodes }),
    []
  );

  const updateMention = useCallback(
    (mention: Episode["mentions"][0]) =>
      dispatch({ type: actionTypes.UPDATE_MENTION, payload: mention }),
    []
  );

  const patchEpisode = useCallback(
    (episode: Partial<Episode> & { episode_uuid: string }) =>
      dispatch({ type: actionTypes.PATCH_EPISODE, payload: episode }),
    []
  );

  const deleteEpisode = useCallback(
    (episodeUuid: Episode["episode_uuid"]) =>
      dispatch({ type: actionTypes.DELETE_EPISODE, payload: episodeUuid }),
    []
  );

  const share = useCallback(async (episode: Episode) => {
    // Either opens a share modal, or the built-in share context
    // on for example a phone or tablet
    postTrackingData(EVENT.SHARE_MENTION, {
      url: episode.share_url,
      source: episode.audio_url,
    });

    if (navigator && navigator.share && userAgentIsPhonelike()) {
      try {
        // share using the share context
        await navigator.share({
          title: `${episode.title} - All Ears`,
          url: episode.share_url,
        });
      } catch (err) {
        // An error is thrown when the user aborts the dialog

        if (!(err as DOMException).name?.includes?.("AbortError")) {
          // fallback to using the regular modal
          setShareMention(episode);
        }
      }
    } else {
      setShareMention(episode);
    }
  }, []);

  const closeShare = () => {
    setShareMention(null);
  };

  // Get mention data
  useEffect(() => {
    let mounted = true;

    if (uuid || isDashboardPage) {
      const mentionsPromise = isDashboardPage
        ? getLatestMentions(filters)
        : getSearchMentions(uuid, filters);

      setLoading(true);
      setMentions([]);
      setErrors([]);
      setMeta(null);
      mentionsPromise
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.statusText);
          }

          if (!mounted) return;
          setMeta(response.data.meta);

          // set date breakpoints
          const { episodes } = response.data;

          setMentions(episodes);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          // Checking mounted here will make sure we're not showing an error message when a user is simply leaving the view.
          mounted && setErrors([e]);
        });
    } else if (slug) {
      setLoading(true);
      getEpisodes({
        ...filters,
        tag: slug,
      }).then(({ data, ok }) => {
        if (!mounted) return;
        if (ok && data) {
          setMentions(data.episodes);
          setMeta(data.meta);
          setLoading(false);
        }
      });
    } else if (isFavoritePage) {
      setLoading(true);
      getEpisodes({
        ...filters,
        tag: "favorited",
      }).then(({ data, ok }) => {
        if (!mounted) return;
        if (ok && data) {
          setMentions(data.episodes);
          setMeta(data.meta);
          setLoading(false);
        }
      });
    }
    return () => {
      mounted = false;
    };
  }, [filters, slug, setMentions, isFavoritePage, isDashboardPage, uuid]);

  return (
    <EpisodesContext.Provider
      value={{
        episodes: state.episodes,
        loading,
        meta,
        errors,
        tag: activeTag,
        alert: activeAlert,
        shareMention,
        share,
        closeShare,
        setMentions,
        deleteEpisode,
        updateMention,
        patchEpisode,
      }}
    >
      {children}
    </EpisodesContext.Provider>
  );
};
export default EpisodesProvider;
